'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Logo } from '@/components/ui/Logo'
import { useFavorites } from '@/context/FavoritesContext'
import { cn } from '@/lib/utils'

const NAV = [
  { href: '/', label: 'Ana Sayfa' },
  { href: '/oner', label: 'Bana Bir Şey Bul' },
  { href: '/kesfet', label: 'Kendim Seçeceğim' },
]

export function Header() {
  const pathname = usePathname()
  const router = useRouter()
  const { favorites } = useFavorites()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false)
    setSearchOpen(false)
  }, [pathname])

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (q) router.push(`/ara?q=${encodeURIComponent(q)}`)
    setSearchOpen(false)
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 transition-all duration-300',
        scrolled
          ? 'border-b border-white/10 bg-ink-900/90 backdrop-blur-md'
          : 'bg-gradient-to-b from-black/80 to-transparent'
      )}
    >
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo className="text-xl sm:text-2xl" />
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => {
              const active = n.href === '/' ? pathname === '/' : pathname.startsWith(n.href)
              return (
                <Link
                  key={n.href}
                  href={n.href}
                  className={cn(
                    'rounded-full px-3 py-2 text-sm font-medium transition-colors',
                    active ? 'text-white' : 'text-neutral-400 hover:text-white'
                  )}
                >
                  {n.label}
                </Link>
              )
            })}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="hidden items-center sm:flex">
            {searchOpen ? (
              <form onSubmit={submitSearch} className="flex items-center">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Film veya dizi ara..."
                  className="w-56 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-crimson-500 focus:outline-none"
                />
              </form>
            ) : (
              <button
                aria-label="Ara"
                onClick={() => setSearchOpen(true)}
                className="grid h-9 w-9 place-items-center rounded-full text-neutral-300 transition hover:bg-white/10 hover:text-white"
              >
                <SearchIcon />
              </button>
            )}
          </div>

          {/* Favorites */}
          <Link
            href="/favoriler"
            aria-label="Favorilerim"
            className="relative grid h-9 w-9 place-items-center rounded-full text-neutral-300 transition hover:bg-white/10 hover:text-white"
          >
            <HeartIcon />
            {favorites.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-crimson-600 px-1 text-[10px] font-bold text-white">
                {favorites.length}
              </span>
            )}
          </Link>

          {/* Mobile menu toggle */}
          <button
            aria-label="Menü"
            onClick={() => setMenuOpen((v) => !v)}
            className="grid h-9 w-9 place-items-center rounded-full text-neutral-200 transition hover:bg-white/10 md:hidden"
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="animate-fade-in-fast border-t border-white/10 bg-ink-900/95 backdrop-blur-md md:hidden">
          <div className="container-page space-y-1 py-4">
            <form onSubmit={submitSearch} className="mb-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Film veya dizi ara..."
                className="w-full rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-neutral-500 focus:border-crimson-500 focus:outline-none"
              />
            </form>
            {NAV.map((n) => (
              <Link
                key={n.href}
                href={n.href}
                className="block rounded-lg px-3 py-2.5 text-base font-medium text-neutral-200 hover:bg-white/5"
              >
                {n.label}
              </Link>
            ))}
            <Link
              href="/favoriler"
              className="block rounded-lg px-3 py-2.5 text-base font-medium text-neutral-200 hover:bg-white/5"
            >
              Favorilerim
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}
function HeartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  )
}
function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}
function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  )
}
